/** นำเข้าสินค้าจาก Excel (D-184) — ตรวจก่อน (preview ไม่เขียนอะไร) แล้วค่อยนำเข้า; ผิดแถวเดียว = ไม่สร้างเลย */

import { useState } from 'react';

import {
  PRODUCT_IMPORT_PATH,
  uploadProductWorkbook,
  type ImportRowError,
  type ProductImportOutcome,
} from '../../lib/backend/records';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';
import { stockFailureText } from '../../lib/inventory/stock';
import { buildApiUrl, getConfiguredApiBaseUrl } from '../../lib/apiBase';
import { downloadFile } from '../../lib/http/fileTransfer';

export interface ProductImportScreenProps {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl?: string;
  readonly fetchImpl?: typeof fetch;
}

export function ProductImportScreen({ permissions, apiBaseUrl, fetchImpl }: ProductImportScreenProps) {
  const io = {
    ...(apiBaseUrl === undefined ? {} : { apiBaseUrl }),
    ...(fetchImpl === undefined ? {} : { fetchImpl }),
  };
  const canImport = can(permissions, 'PRODUCT_MASTER', 'CREATE');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<readonly ImportRowError[]>([]);
  const [outcome, setOutcome] = useState<ProductImportOutcome | null>(null);

  const run = async (commit: boolean) => {
    if (file === null) {
      setError('เลือกไฟล์ .xlsx ก่อน');
      return;
    }
    setBusy(true);
    setError(null);
    setRowErrors([]);
    setOutcome(null);
    const result = await uploadProductWorkbook(file, commit, io);
    setBusy(false);
    if (result.status === 'refused') {
      setError(result.message);
      setRowErrors(result.errors);
      return;
    }
    if (result.status !== 'ready') {
      setError(stockFailureText(result));
      return;
    }
    setRowErrors(result.outcome.errors);
    setOutcome(result.outcome);
  };

  const templateUrl = buildApiUrl(apiBaseUrl ?? getConfiguredApiBaseUrl(), `${PRODUCT_IMPORT_PATH}/template`);

  const downloadTemplate = async () => {
    setError(null);
    const result = await downloadFile(templateUrl, 'product-import-template.xlsx', {
      ...(fetchImpl === undefined ? {} : { fetchImpl }),
    });
    if (result.status === 'failed') {
      setError(`ดาวน์โหลดไฟล์ตัวอย่างไม่สำเร็จ (${result.response.status})`);
    } else if (result.status === 'network-error') {
      setError(`เชื่อมต่อไม่ได้: ${result.detail}`);
    }
  };


  return (
    <div className="cap-page" data-testid="product-import">
      <div className="cap-page__head">
        <h1 className="cap-page__title">นำเข้าสินค้าจาก Excel</h1>
      </div>
      <p className="cap-notice__text">
        หนึ่งแถว = หนึ่งสินค้าในหนึ่งโรงงาน สร้างเป็น DRAFT รออนุมัติ — ถ้ามีแถวผิดแม้แถวเดียวจะไม่สร้างสินค้าใดเลย{' '}
        <button
          type="button"
          className="cap-button cap-button--ghost"
          data-testid="product-import-template"
          onClick={() => void downloadTemplate()}
        >
          ดาวน์โหลดไฟล์ตัวอย่าง
        </button>
      </p>
      {!canImport && (
        <p className="cap-notice__note" role="status" data-testid="product-import-no-create">
          ต้องมีสิทธิ์สร้างข้อมูลสินค้า (PRODUCT_MASTER CREATE) จึงนำเข้าได้
        </p>
      )}
      <label className="cap-field">
        <span className="cap-field__label">ไฟล์ .xlsx</span>
        <input
          className="cap-input"
          type="file"
          accept=".xlsx"
          aria-label="ไฟล์ .xlsx"
          onChange={(e) => {
            setFile(e.target.files?.[0] ?? null);
            setOutcome(null);
            setRowErrors([]);
            setError(null);
          }}
        />
      </label>
      <div className="cap-form__actions">
        <button type="button" className="cap-button cap-button--ghost" disabled={busy || !canImport} onClick={() => void run(false)}>
          ตรวจไฟล์ (ไม่บันทึก)
        </button>
        <button type="button" className="cap-button cap-button--primary" disabled={busy || !canImport} onClick={() => void run(true)}>
          นำเข้า
        </button>
      </div>

      {error !== null && (
        <p className="cap-notice__note" role="alert" data-testid="product-import-error">
          {error}
        </p>
      )}
      {outcome !== null && rowErrors.length === 0 && (
        <p className="cap-notice__text" role="status" data-testid="product-import-result">
          {outcome.created
            ? `นำเข้าแล้ว ${outcome.product_codes.length} รายการ`
            : `ไฟล์ผ่านการตรวจ ${outcome.row_count} แถว (ยังไม่ได้บันทึก)`}
        </p>
      )}
      {rowErrors.length > 0 && (
        <table className="cap-table" data-testid="product-import-errors">
          <thead>
            <tr>
              <th>แถว</th>
              <th>คอลัมน์</th>
              <th>กฎ</th>
              <th>ข้อความ</th>
            </tr>
          </thead>
          <tbody>
            {rowErrors.map((e, i) => (
              <tr key={i}>
                <td>{e.row}</td>
                <td>{e.column || '—'}</td>
                <td>{e.rule}</td>
                <td>{e.message}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
